/**
 * Utility functions for handling status bar styling on iOS devices
 */

// Detect if running on iOS
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

// Detect if running in standalone mode (PWA)
export const isStandalone = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator as any).standalone === true;
};

// Set status bar color dynamically
export const setStatusBarColor = (color: string): void => {
  if (isIOS() && isStandalone()) {
    // Create or update meta tag for theme-color
    let metaThemeColor = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = color;
    
    // Also update the CSS custom property
    document.documentElement.style.setProperty('--status-bar-color', color);
  }
};

// Initialize status bar for the app
export const initializeStatusBar = (): void => {
  if (isIOS()) {
    // Set the same status bar color for both themes
    const statusBarColor = '#334155';
    setStatusBarColor(statusBarColor);
    
    // Listen for theme changes (but keep same color)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          // Always use the same color regardless of theme
          setStatusBarColor('#334155');
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  }
};

// Force status bar styling on iOS
export const forceStatusBarStyling = (): void => {
  if (isIOS()) {
    // Add iOS-specific class to body
    document.body.classList.add('ios-device');
    
    // Add status bar overlay class to App
    const appElement = document.querySelector('.App') as HTMLElement;
    if (appElement) {
      appElement.classList.add('has-status-bar-overlay');
    }
    
    // Ensure status bar area is properly styled
    const statusBarArea = document.querySelector('.App::before') as HTMLElement;
    if (statusBarArea) {
      statusBarArea.style.background = 'var(--status-bar-color)';
    }
    
    // Add viewport-fit=auto support
    const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    if (viewport) {
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=auto';
    }
  }
};

// Handle safe area insets
export const handleSafeAreaInsets = (): void => {
  if (isIOS()) {
    // Set CSS custom properties for safe area insets
    const root = document.documentElement;
    const safeAreaTop = getComputedStyle(root).getPropertyValue('env(safe-area-inset-top)');
    const safeAreaBottom = getComputedStyle(root).getPropertyValue('env(safe-area-inset-bottom)');
    
    root.style.setProperty('--safe-area-inset-top', safeAreaTop || '0px');
    root.style.setProperty('--safe-area-inset-bottom', safeAreaBottom || '0px');
    
    // Force fullscreen mode on iOS PWA
    if (isStandalone()) {
      // Prevent address bar from showing
      document.body.style.height = '100%';
      document.body.style.height = '-webkit-fill-available';
      document.documentElement.style.height = '100%';
      document.documentElement.style.height = '-webkit-fill-available';
      
      // Prevent bounce scrolling
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
      
      // Hide browser UI
      const metaViewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
      if (metaViewport) {
        metaViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=auto';
      }
    }
  }
};
